import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../../api/endpoints';

const StepScreenshot = ({ 
  stepScreenshot, 
  setStepScreenshot, 
  screenshotPreview, 
  setScreenshotPreview,
  isUploadingScreenshot,
  setIsUploadingScreenshot,
  stepId,
  existingScreenshotUrl, // Add this prop to display existing screenshots
  onRemoveExistingScreenshot // Add this callback to remove existing screenshots
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [removedExistingUrl, setRemovedExistingUrl] = useState(null);
  
  const displayUrl = screenshotPreview || (existingScreenshotUrl && existingScreenshotUrl !== removedExistingUrl ? existingScreenshotUrl : null);

  // Reset removedExistingUrl when existingScreenshotUrl changes
  useEffect(() => {
    if (existingScreenshotUrl !== removedExistingUrl) {
      setRemovedExistingUrl(null);
    }
  }, [existingScreenshotUrl, removedExistingUrl]);

  const handleFileUpload = async (file) => {
    if (file && file.type.startsWith('image/')) {
      // Set the file and preview immediately
      setStepScreenshot(file);
      setScreenshotPreview(URL.createObjectURL(file));
      
      // Upload the image immediately
      try {
        setIsUploadingScreenshot(true);
        
        const result = await apiEndpoints.uploadStepScreenshot(file, stepId || `temp-${Date.now()}`);
        
        // Store the URL for later use when saving the step
        if (result.url) {
          setStepScreenshot({ ...file, uploadedUrl: result.url });
        }
      } catch (error) {
        console.error('📸 Upload error:', error);
      } finally {
        setIsUploadingScreenshot(false);
      }
    }
  };

  const handleScreenshotUpload = async (event) => {
    const file = event.target.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const removeScreenshot = () => {
    // Clear local screenshot state
    setStepScreenshot(null);
    setScreenshotPreview(null);
    
    // Mark the existing URL as removed
    if (existingScreenshotUrl) {
      setRemovedExistingUrl(existingScreenshotUrl);
    }
    
    // If there's an existing screenshot and a callback, remove it from the parent
    if (existingScreenshotUrl && onRemoveExistingScreenshot) {
      onRemoveExistingScreenshot();
    }
    
    // Clear any object URLs to prevent memory leaks
    if (screenshotPreview && screenshotPreview.startsWith('blob:')) {
      URL.revokeObjectURL(screenshotPreview);
    }
  };

  return (
    <div className="space-y-3 mb-2">
      <label className="block text-sm font-medium text-gray-700">
        Element Screenshot (Optional)
        <span className="text-xs text-gray-500 block mt-1">
          Add a screenshot to help AI identify the element more accurately
        </span>
      </label>
      
      {!displayUrl ? (
        <div 
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            isDragOver 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleScreenshotUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          <div className="flex flex-col items-center gap-3">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            
            <div>
              <p className="text-lg font-medium text-gray-900 mb-1">
                {isDragOver ? 'Drop image here' : 'Upload screenshot'}
              </p>
              <p className="text-sm text-gray-500">
                Drag and drop an image file, or click to select
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Supports PNG, JPG, GIF up to 10MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative inline-block">
            <img
              src={displayUrl}
              alt="Step screenshot"
              className="max-w-full h-32 object-contain border border-gray-200 rounded-lg shadow-sm"
            />
            <button
              type="button"
              onClick={removeScreenshot}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            {isUploadingScreenshot ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span>Uploading screenshot...</span>
              </div>
            ) : existingScreenshotUrl && !screenshotPreview ? (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Screenshot saved</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Screenshot ready to upload</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepScreenshot;
